/**
 * Onboarding System
 * Shows tooltips for first-time users to guide them through the interface
 * Only shows after successful authentication
 */

import { useState, useEffect, createContext, useContext, type ReactNode } from 'react';
import { ArrowRight, ArrowLeft, CheckCircle, Zap } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

// Onboarding steps definition
interface OnboardingStep {
  id: string;
  target?: string; // CSS selector for the target element
  title: string;
  description: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

const onboardingSteps: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'WELCOME TO GRIDINSPECT',
    description: 'This quick tour will help you get started with inspecting power lines. Let\'s walk through the main features.',
  },
  {
    id: 'sidebar',
    target: '[data-onboarding="sidebar"]',
    title: 'NAVIGATION PANEL',
    description: 'Use the sidebar to navigate between Dashboard, Projects, and Settings. Recent projects appear below for quick access.',
    position: 'right',
  },
  {
    id: 'search',
    title: 'QUICK SEARCH',
    description: 'Press Cmd+K (or Ctrl+K) anytime to open the command palette. Search for projects or quickly navigate anywhere.',
  },
  {
    id: 'shortcuts',
    title: 'KEYBOARD SHORTCUTS',
    description: 'Press ? anytime to see all available keyboard shortcuts. They\'ll help you work faster!',
  },
  {
    id: 'complete',
    title: 'READY TO INSPECT',
    description: 'Start by creating a new project or opening an existing one. Good luck with your inspections!',
  },
];

const ONBOARDING_KEY = 'gridinspect_onboarding_completed';

// Context for onboarding state
interface OnboardingContextType {
  isOnboarding: boolean;
  currentStep: number;
  startOnboarding: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipOnboarding: () => void;
  completeOnboarding: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}

// Provider component
export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const { isAuthenticated } = useAuthStore();

  // Check if user has completed onboarding - only after authentication
  useEffect(() => {
    if (!isAuthenticated) {
      setIsOnboarding(false);
      return;
    }
    
    const completed = localStorage.getItem(ONBOARDING_KEY);
    if (!completed) {
      // Auto-start onboarding for new users after a short delay
      const timer = setTimeout(() => {
        setIsOnboarding(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated]);

  const startOnboarding = () => {
    setCurrentStep(0);
    setIsOnboarding(true);
  };

  const nextStep = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeOnboarding();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const skipOnboarding = () => {
    setIsOnboarding(false);
    localStorage.setItem(ONBOARDING_KEY, 'true');
  };

  const completeOnboarding = () => {
    setIsOnboarding(false);
    localStorage.setItem(ONBOARDING_KEY, 'true');
  };

  return (
    <OnboardingContext.Provider
      value={{
        isOnboarding,
        currentStep,
        startOnboarding,
        nextStep,
        prevStep,
        skipOnboarding,
        completeOnboarding,
      }}
    >
      {children}
      {isOnboarding && isAuthenticated && <OnboardingOverlay />}
    </OnboardingContext.Provider>
  );
}

// Overlay component that shows the tooltip - Dark theme
function OnboardingOverlay() {
  const { currentStep, nextStep, prevStep, skipOnboarding, completeOnboarding } = useOnboarding();
  const step = onboardingSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === onboardingSteps.length - 1;

  return (
    <div className="fixed inset-0 z-[200]">
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Tooltip */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-slate-900 border border-slate-700/50 rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
          {/* Progress bar */}
          <div className="h-1 bg-slate-800">
            <div 
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${((currentStep + 1) / onboardingSteps.length) * 100}%` }}
            />
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Icon */}
            <div className="w-12 h-12 bg-blue-500/20 border border-blue-500/30 rounded-xl flex items-center justify-center mb-4">
              {isLastStep ? (
                <CheckCircle className="w-6 h-6 text-blue-400" />
              ) : (
                <Zap className="w-6 h-6 text-blue-400" />
              )}
            </div>

            {/* Step indicator */}
            <div className="text-xs font-mono text-slate-500 mb-2 tracking-wider">
              STEP {currentStep + 1} OF {onboardingSteps.length}
            </div>

            {/* Title & Description */}
            <h3 className="text-lg font-mono font-bold text-slate-100 mb-2 tracking-wide">
              {step.title}
            </h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              {step.description}
            </p>
          </div>

          {/* Actions */}
          <div className="px-6 py-4 bg-slate-800/50 border-t border-slate-700/50 flex items-center justify-between">
            <button
              onClick={skipOnboarding}
              className="text-sm font-mono text-slate-500 hover:text-slate-300 transition-colors tracking-wide"
            >
              SKIP TOUR
            </button>

            <div className="flex items-center gap-2">
              {!isFirstStep && (
                <button
                  onClick={prevStep}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-mono text-slate-400 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  BACK
                </button>
              )}
              <button
                onClick={isLastStep ? completeOnboarding : nextStep}
                className="flex items-center gap-1 px-4 py-2 text-sm font-mono font-bold bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors tracking-wide"
              >
                {isLastStep ? (
                  <>
                    START
                    <CheckCircle className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    NEXT
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Button to restart onboarding (for settings page)
export function RestartOnboardingButton() {
  const { startOnboarding } = useOnboarding();

  const handleRestart = () => {
    localStorage.removeItem(ONBOARDING_KEY);
    startOnboarding();
  };

  return (
    <button
      onClick={handleRestart}
      className="flex items-center gap-2 px-4 py-2 text-sm font-mono text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
    >
      <Zap className="w-4 h-4" />
      RESTART TOUR
    </button>
  );
}
